import {
    ButtonGradientAi,
    ButtonSecondary,
    Colors,
    FIXED_CIRCLE_DIMENSIONS,
    FOCUS_RING_NO_RADIUS,
    Icon,
    Modal,
    MultilineInput,
    RadioGroup,
    useDialogFocus,
} from '@amelio/ui-library';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ObjectiveDraftPreview from './ObjectiveDraftPreview';

import type { GenerateObjectiveDraftOutcome } from '@/pages/Objectives/hooks/useGenerateObjectiveDraft';
import type { ObjectiveDraft, ObjectiveDraftLanguage } from '@/types/objectives';

import { AiPromptModalHeader } from '@/components/AiPromptModal/components/AiPromptModalHeader';
import { AiPromptModalImage } from '@/components/AiPromptModal/components/AiPromptModalImage';

/** Same floor as the backend validator: below this the intent carries nothing to draft from. */
export const INTENT_MIN_LENGTH = 10;
/** Same cap as the backend validator: each generation is a paid call. */
const INTENT_MAX_LENGTH = 1000;

/** The interface language picks the default output language; anything but French means English. */
const defaultDraftLanguage = (interfaceLanguage: string): ObjectiveDraftLanguage =>
    interfaceLanguage.startsWith('fr') ? 'fr' : 'en';

interface GenerateObjectiveAiModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Runs the real generation and says how it ended (draft, failure, allowance spent). */
    onGenerate: (
        userIntent: string,
        language: ObjectiveDraftLanguage,
    ) => Promise<GenerateObjectiveDraftOutcome>;
    /** Applies the previewed draft to the form. The modal closes itself afterwards. */
    onApply: (draft: ObjectiveDraft) => void;
    /** When true, the form already has key results: the preview says the suggestions stay out. */
    hasKeyResults: boolean;
    dataTestId?: string;
}

/**
 * The prototype's "Générer un objectif avec l'IA" window (`GenerateObjectiveAIModal.tsx`): the
 * shared AI-modal frame (header, illustration, close), one free-text question, then -- unlike the
 * prototype, which applies the result at once -- a preview the manager applies or discards, so a
 * title already typed is never overwritten unseen. Controlled `Modal`, like `PillarVisionModal`.
 */
export default function GenerateObjectiveAiModal({
    isOpen,
    onClose,
    onGenerate,
    onApply,
    hasKeyResults,
    dataTestId = 'objective-form-ai-modal',
}: GenerateObjectiveAiModalProps) {
    const { t, i18n } = useTranslation();
    // The shared dialog focus contract: focus moves in on opening, Tab stays inside, and focus
    // goes back to the button that opened the window once it closes.
    const { dialogRef, handleKeyDown } = useDialogFocus(isOpen);
    const [intent, setIntent] = useState('');
    const [language, setLanguage] = useState<ObjectiveDraftLanguage>(() =>
        defaultDraftLanguage(i18n.language),
    );
    const [isGenerating, setIsGenerating] = useState(false);
    const [draft, setDraft] = useState<ObjectiveDraft | null>(null);
    // `failed` is worded as "try again"; `limitReached` as "come back tomorrow" and locks the button.
    const [failure, setFailure] = useState<
        { kind: 'failed' } | { kind: 'limitReached'; limit: number } | null
    >(null);
    // Bumped on every open: a generation still running when the modal was closed must not land in
    // a fresh session.
    const sessionRef = useRef(0);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        sessionRef.current += 1;
        setIntent('');
        setLanguage(defaultDraftLanguage(i18n.language));
        setDraft(null);
        setFailure(null);
        setIsGenerating(false);
    }, [isOpen, i18n.language]);

    const trimmedIntent = intent.trim();
    const isLimitReached = failure?.kind === 'limitReached';
    const canGenerate =
        trimmedIntent.length >= INTENT_MIN_LENGTH && !isGenerating && !isLimitReached;

    const handleGenerate = async () => {
        if (!canGenerate) {
            return;
        }
        const session = sessionRef.current;
        setFailure(null);
        setIsGenerating(true);
        const outcome = await onGenerate(trimmedIntent, language);
        if (session !== sessionRef.current) {
            return;
        }
        setIsGenerating(false);
        if (outcome.kind !== 'draft') {
            setFailure(outcome);
            return;
        }
        setDraft(outcome.draft);
    };

    const languageOptions = [
        { value: 'fr', label: t('objectives.form.ai.languageFr') },
        { value: 'en', label: t('objectives.form.ai.languageEn') },
    ];

    const handleApply = () => {
        if (!draft) {
            return;
        }
        onApply(draft);
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            // A generation cannot be cancelled: Escape and the backdrop wait for it to end.
            preventDefault={isGenerating}
            className={'max-w-8xl w-[1152px]'}
            padding={'none'}
            radius={'2xl'}
            shadow={'lg'}
            zIndex={60}
            dataTestId={dataTestId}
        >
            <div
                ref={dialogRef}
                onKeyDown={handleKeyDown}
                role={'dialog'}
                aria-modal={'true'}
                aria-labelledby={`${dataTestId}-title`}
                className={'relative flex flex-col md:flex-row'}
                onClick={(event) => event.stopPropagation()}
            >
                <div className={'flex-1 p-6 md:p-10 lg:p-13'}>
                    <AiPromptModalHeader
                        titleKey={'objectives.form.ai.modalTitle'}
                        titleId={`${dataTestId}-title`}
                    />

                    {draft === null ? (
                        <div className={'animate-fade-in-up flex flex-col gap-5'}>
                            <div className={'flex items-center gap-2'}>
                                <Icon
                                    name={'essential-flash'}
                                    size={'lg'}
                                    color={Colors.BASE_600}
                                />
                                <label
                                    htmlFor={`${dataTestId}-intent`}
                                    className={
                                        'text-navy font-semibold max-md:text-[18px] md:text-[20px]'
                                    }
                                >
                                    {t('objectives.form.ai.intentLabel')}
                                </label>
                            </div>

                            <MultilineInput
                                id={`${dataTestId}-intent`}
                                value={intent}
                                onChange={setIntent}
                                rows={4}
                                maxLength={INTENT_MAX_LENGTH}
                                showCharacterCount
                                disabled={isGenerating}
                                placeholder={t('objectives.form.ai.intentPlaceholder')}
                                ariaDescribedBy={`${dataTestId}-hint`}
                                dataTestId={`${dataTestId}-intent`}
                                className={'border-obj-line border p-4'}
                            />

                            <p
                                id={`${dataTestId}-hint`}
                                className={'text-obj-muted text-[12px]'}
                            >
                                {t('objectives.form.ai.hint')}
                            </p>

                            <fieldset className={'flex flex-col gap-2'}>
                                <legend className={'text-navy text-[14px] font-medium'}>
                                    {t('objectives.form.ai.languageLabel')}
                                </legend>
                                <RadioGroup
                                    name={`${dataTestId}-language`}
                                    options={languageOptions}
                                    value={language}
                                    onChange={(value) =>
                                        setLanguage(value as ObjectiveDraftLanguage)
                                    }
                                    orientation={'horizontal'}
                                    size={'sm'}
                                    disabled={isGenerating}
                                    dataTestId={`${dataTestId}-language`}
                                />
                            </fieldset>

                            {failure && (
                                <p
                                    role={'alert'}
                                    className={'text-error text-[14px]'}
                                    data-testid={`${dataTestId}-error`}
                                >
                                    {failure.kind === 'limitReached'
                                        ? t('objectives.form.ai.limitReached', {
                                              limit: failure.limit,
                                          })
                                        : t('objectives.form.ai.error')}
                                </p>
                            )}

                            <div className={'mt-4 flex items-center justify-end gap-3'}>
                                <ButtonSecondary
                                    type={'button'}
                                    size={'md'}
                                    onClick={onClose}
                                    disabled={isGenerating}
                                    dataTestId={`${dataTestId}-cancel`}
                                >
                                    {t('objectives.form.ai.cancel')}
                                </ButtonSecondary>

                                <ButtonGradientAi
                                    type={'button'}
                                    size={'md'}
                                    onClick={handleGenerate}
                                    disabled={!canGenerate}
                                    loading={isGenerating}
                                    leftIcon={
                                        <Icon
                                            name={'support-magic-star'}
                                            size={'md'}
                                            color={Colors.WHITE}
                                        />
                                    }
                                    dataTestId={`${dataTestId}-generate`}
                                >
                                    {t(
                                        isGenerating
                                            ? 'objectives.form.ai.generating'
                                            : 'objectives.form.ai.generate',
                                    )}
                                </ButtonGradientAi>
                            </div>
                        </div>
                    ) : (
                        <ObjectiveDraftPreview
                            draft={draft}
                            keyResultsKept={hasKeyResults && draft.keyResults.length > 0}
                            onRegenerate={() => setDraft(null)}
                            onApply={handleApply}
                            dataTestId={dataTestId}
                        />
                    )}
                </div>

                <AiPromptModalImage
                    isGenerating={isGenerating}
                    imageAltKey={'objectives.form.ai.imageAlt'}
                    overlayGeneratingKey={'objectives.form.ai.generating'}
                    overlayMessageKey={'objectives.form.ai.generatingMessage'}
                />

                <button
                    type={'button'}
                    onClick={onClose}
                    disabled={isGenerating}
                    aria-label={t('objectives.form.ai.close')}
                    data-testid={`${dataTestId}-close`}
                    className={`absolute top-4 right-4 z-20 flex ${FIXED_CIRCLE_DIMENSIONS.lg} hover:bg-base-50 items-center justify-center rounded-full bg-white shadow-md disabled:cursor-not-allowed disabled:opacity-50 ${FOCUS_RING_NO_RADIUS}`}
                >
                    <Icon
                        name={'essential-close'}
                        size={'md'}
                        color={Colors.BASE_400}
                    />
                </button>
            </div>
        </Modal>
    );
}
